/**
 * Данные эпик-вью: дерево детей, их состояние и что происходило в GitLab.
 *
 * Дети берутся тем же фильтром по родителю, что и подзадачи карточки, — двумя
 * уровнями: истории эпика и подзадачи этих историй. Глубже иерархия не бывает
 * по правилам типов, поэтому рекурсии нет, а внуки грузятся параллельно, а не
 * по одному на строку.
 */
import { listActions, toReleases, toStands, type Release, type Stand } from '@/features/devops/api'
import { listChildren } from '@/features/issue/links'
import { getSummary, type SummaryRow } from '@/features/pipelines/api'

import type { IssueRow } from '@/features/issues/api'
import type { ProjectColumn } from '@/features/projects/api'

/** Узел дерева: ребёнок эпика и его подзадачи */
export interface EpicNode {
  issue: IssueRow
  children: IssueRow[]
}

/** Подзадачи бывают только у того, кто сам может быть родителем */
const HAS_CHILDREN = ['ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG']

export const loadTree = async (projectId: string, epicId: string): Promise<EpicNode[]> => {
  const level = await listChildren(projectId, epicId)

  return Promise.all(
    level.map(async (issue) => ({
      issue,
      children: HAS_CHILDREN.includes(issue.type) ? await listChildren(projectId, issue.id) : [],
    })),
  )
}

/** Все задачи эпика плоским списком — по ним считается прогресс */
export const flatten = (nodes: EpicNode[]): IssueRow[] =>
  nodes.flatMap((node) => [node.issue, ...node.children])

/** Разбивка задач эпика по категориям статусов — сегменты полосы прогресса */
export interface Progress {
  total: number
  done: number
  inProgress: number
  open: number
}

/**
 * Считаем по категории колонки, а не по её названию: «Готово» в одном проекте
 * зовётся «Выкачено», и сравнение строк врало бы у всех, кто переименовал
 * колонку.
 */
export const toProgress = (issues: IssueRow[], columns: ProjectColumn[]): Progress => {
  const categoryOf = (issue: IssueRow) =>
    columns.find((column) => column.id === issue.columnId)?.statusCategory ?? ''

  const done = issues.filter((issue) => categoryOf(issue) === 'STATUS_CATEGORY_DONE').length
  const inProgress = issues.filter(
    (issue) => categoryOf(issue) === 'STATUS_CATEGORY_IN_PROGRESS',
  ).length

  return { total: issues.length, done, inProgress, open: issues.length - done - inProgress }
}

/**
 * Последний прогон на каждый сервис эпика. Сводка отдаёт по одному прогону на
 * пару «сервис и ветка» — здесь сворачиваем до сервиса: на эпик смотрят, чтобы
 * понять, где красное, а не читать все ветки.
 */
export const byService = (rows: SummaryRow[]): SummaryRow[] => {
  const latest = new Map<string, SummaryRow>()

  for (const row of rows) {
    const current = latest.get(row.run.projectPath)

    if (!current) {
      latest.set(row.run.projectPath, row)

      continue
    }

    // Упавшее держим до последнего: сервис с красным прогоном остаётся
    // красным, даже если следом прошёл зелёный прогон другой ветки.
    if (current.run.status === 'failed') continue

    const newer = new Date(row.run.createdAt).getTime() > new Date(current.run.createdAt).getTime()

    if (row.run.status === 'failed' || newer) latest.set(row.run.projectPath, row)
  }

  return [...latest.values()]
}

export const loadPipelines = async (projectId: string, epicId: string): Promise<SummaryRow[]> =>
  byService(await getSummary(projectId, epicId))

/** Стенды и релизы эпика: журнал знает задачу каждой операции */
export interface EpicOps {
  stands: Stand[]
  releases: Release[]
}

export const loadOps = async (issueIds: string[]): Promise<EpicOps> => {
  const own = new Set(issueIds)
  const actions = (await listActions()).filter((action) => own.has(action.issueId))

  return { stands: toStands(actions), releases: toReleases(actions).slice(0, 3) }
}
